import { Injectable, inject, signal, computed } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ViewportService } from './viewport.service';

type SidebarState = 'closed' | 'open' | 'static' | 'closing';
type SidebarType = 'chat' | 'main';

@Injectable({
  providedIn: 'root',
})
export class SidebarService {
  private chatSidebarState = signal<SidebarState>('closed');
  private mainSidebarState = signal<SidebarState>('closed');
  private viewportService = inject(ViewportService);

  constructor() {
    this.viewportService.isDesktopPlusUnique$
      .pipe(takeUntilDestroyed())
      .subscribe((isDesktop) => {
        this.chatSidebarState.set(isDesktop ? 'static' : 'closed');
        // Main sidebar is always closed on desktop (uses different layout)
        this.mainSidebarState.set('closed');
      });
  }

  // ===========================
  // Chat Sidebar Properties
  // ===========================
  isChatSidebarOpen = computed(() => this.chatSidebarState() === 'open');
  isChatSidebarClosing = computed(() => this.chatSidebarState() === 'closing');
  isChatSidebarStatic = computed(() => this.chatSidebarState() === 'static');
  isChatSidebarVisible = computed(
    () =>
      this.chatSidebarState() === 'open' ||
      this.chatSidebarState() === 'static' ||
      this.chatSidebarState() === 'closing',
  );
  showChatSidebarBackdrop = computed(
    () => this.chatSidebarState() === 'open' || this.chatSidebarState() === 'closing',
  );

  // ===========================
  // Main Sidebar Properties
  // ===========================
  isMainSidebarOpen = computed(() => this.mainSidebarState() === 'open');
  isMainSidebarClosing = computed(() => this.mainSidebarState() === 'closing');
  isMainSidebarVisible = computed(
    () =>
      this.mainSidebarState() === 'open' ||
      this.mainSidebarState() === 'closing',
  );
  showMainSidebarBackdrop = computed(
    () => this.mainSidebarState() === 'open' || this.mainSidebarState() === 'closing',
  );

  // ===========================
  // Chat Sidebar Methods
  // ===========================
  openChatSidebar(): void {
    this.chatSidebarState.set('open');
  }

  closeChatSidebar(): void {
    if (this.chatSidebarState() === 'open') {
      this.chatSidebarState.set('closing');
      setTimeout(() => {
        if (this.chatSidebarState() === 'closing') {
          this.chatSidebarState.set('closed');
        }
      }, 400);
    }
  }

  toggleChatSidebar(): void {
    this.chatSidebarState.update((state) =>
      state === 'open' ? 'closing' : 'open',
    );
    if (this.chatSidebarState() === 'closing') {
      setTimeout(() => {
        if (this.chatSidebarState() === 'closing') {
          this.chatSidebarState.set('closed');
        }
      }, 400);
    }
  }

  // ===========================
  // Main Sidebar Methods
  // ===========================
  openMainSidebar(): void {
    this.mainSidebarState.set('open');
  }

  closeMainSidebar(): void {
    if (this.mainSidebarState() === 'open') {
      this.mainSidebarState.set('closing');
      setTimeout(() => {
        if (this.mainSidebarState() === 'closing') {
          this.mainSidebarState.set('closed');
        }
      }, 400);
    }
  }

  toggleMainSidebar(): void {
    this.mainSidebarState.update((state) =>
      state === 'open' ? 'closing' : 'open',
    );
    if (this.mainSidebarState() === 'closing') {
      setTimeout(() => {
        if (this.mainSidebarState() === 'closing') {
          this.mainSidebarState.set('closed');
        }
      }, 400);
    }
  }

  // ===========================
  // Generic Methods
  // ===========================
  openSidebar(type: SidebarType = 'chat'): void {
    if (type === 'main') {
      this.openMainSidebar();
    } else {
      this.openChatSidebar();
    }
  }

  closeSidebar(type: SidebarType = 'chat'): void {
    if (type === 'main') {
      this.closeMainSidebar();
    } else {
      this.closeChatSidebar();
    }
  }

  toggleSidebar(type: SidebarType = 'chat'): void {
    if (type === 'main') {
      this.toggleMainSidebar();
    } else {
      this.toggleChatSidebar();
    }
  }

  // ===========================
  // Deprecated Methods (for backward compatibility)
  // ===========================
  
  /**
   * @deprecated Use `isChatSidebarOpen` instead
   */
  isOpen = this.isChatSidebarOpen;
  
  /**
   * @deprecated Use `isChatSidebarClosing` instead
   */
  isClosing = this.isChatSidebarClosing;
  
  /**
   * @deprecated Use `isChatSidebarStatic` instead
   */
  isStaticMode = this.isChatSidebarStatic;
  
  /**
   * @deprecated Use `showChatSidebarBackdrop` instead
   */
  showBackdrop = this.showChatSidebarBackdrop;
  
  /**
   * @deprecated Use `isChatSidebarVisible` instead
   */
  isVisible = this.isChatSidebarVisible;

  /**
   * @deprecated Use `openChatSidebar()` instead
   */
  open(): void {
    this.openChatSidebar();
  }

  /**
   * @deprecated Use `closeChatSidebar()` instead
   */
  close(): void {
    this.closeChatSidebar();
  }

  /**
   * @deprecated Use `toggleChatSidebar()` instead
   */
  toggle(): void {
    this.toggleChatSidebar();
  }

  // ===========================
  // Deprecated Main Sidebar Methods (for backward compatibility)
  // ===========================
  
  /**
   * @deprecated Use `isMainSidebarOpen` instead
   */
  isMainOpen = this.isMainSidebarOpen;
  
  /**
   * @deprecated Use `isMainSidebarClosing` instead
   */
  isMainClosing = this.isMainSidebarClosing;
  
  /**
   * @deprecated Use `showMainSidebarBackdrop` instead
   */
  showMainBackdrop = this.showMainSidebarBackdrop;
  
  /**
   * @deprecated Use `isMainSidebarVisible` instead
   */
  isMainVisible = this.isMainSidebarVisible;

  /**
   * @deprecated Use `openMainSidebar()` instead
   */
  openMain(): void {
    this.openMainSidebar();
  }

  /**
   * @deprecated Use `closeMainSidebar()` instead
   */
  closeMain(): void {
    this.closeMainSidebar();
  }

  /**
   * @deprecated Use `toggleMainSidebar()` instead
   */
  toggleMain(): void {
    this.toggleMainSidebar();
  }
}